import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Tag,
  Popconfirm,
  Card,
  Typography,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getMqttServers,
  createMqttServer,
  updateMqttServer,
  deleteMqttServer,
} from '../api/mqtt';

const { Title } = Typography;

interface MqttServer {
  id: number;
  name: string;
  url: string;
  port: number;
  useTls?: boolean;
  username?: string;
  password?: string;
  enabled?: boolean;
  isDefault?: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

const MqttServerManagement: React.FC = () => {
  const [servers, setServers] = useState<MqttServer[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingServer, setEditingServer] = useState<MqttServer | null>(null);
  const [form] = Form.useForm();

  // Load data on mount
  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    setLoading(true);
    try {
      const res = await getMqttServers();
      setServers(res.data || []);
    } catch (error) {
      message.error('加载MQTT服务器列表失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingServer(null);
    form.resetFields();
    form.setFieldsValue({
      port: 1883,
      useTls: false,
      enabled: true,
      isDefault: false,
    });
    setModalVisible(true);
  };

  const handleEdit = (record: MqttServer) => {
    setEditingServer(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMqttServer(id);
      message.success('删除成功');
      loadServers();
    } catch (error) {
      message.error('删除失败');
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingServer) {
        await updateMqttServer(editingServer.id, values);
        message.success('更新成功');
      } else {
        await createMqttServer(values);
        message.success('创建成功');
      }
      
      setModalVisible(false);
      loadServers();
    } catch (error) {
      message.error(editingServer ? '更新失败' : '创建失败');
      console.error(error);
    }
  };

  const columns: ColumnsType<MqttServer> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '服务器名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {text}
          {record.isDefault && <Tag color="gold" icon={<CheckCircleOutlined />}>默认</Tag>}
        </Space>
      ),
    },
    {
      title: '地址',
      dataIndex: 'url',
      key: 'url',
      render: (text, record) => `${record.useTls ? 'mqtts' : 'mqtt'}://${text}:${record.port}`,
    },
    {
      title: '认证',
      key: 'auth',
      render: (_, record) => 
        record.username ? (
          <Tag color="blue">{record.username}</Tag>
        ) : (
          <Tag color="default">无认证</Tag>
        ),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>{enabled ? '启用' : '禁用'}</Tag>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个MQTT服务器吗？"
            description="删除后相关的订阅配置将失效"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>MQTT 服务器管理</Title>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadServers}
            >
              刷新列表
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              新建服务器
            </Button>
          </Space>
        </div>

        <Divider />

        <Table
          columns={columns}
          dataSource={servers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Server Modal */}
      <Modal
        title={editingServer ? '编辑MQTT服务器' : '新建MQTT服务器'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            port: 1883,
            useTls: false,
            enabled: true,
            isDefault: false,
          }}
        >
          <Form.Item
            name="name"
            label="服务器名称"
            rules={[{ required: true, message: '请输入服务器名称' }]}
          >
            <Input placeholder="例如：主MQTT服务器" />
          </Form.Item>

          <Form.Item
            name="url"
            label="服务器地址"
            rules={[
              { required: true, message: '请输入服务器地址' },
              { pattern: /^[a-zA-Z0-9._-]+$/, message: '请输入有效的域名或IP地址' },
            ]}
            extra="例如：mqtt.example.com 或 192.168.1.100"
          >
            <Input placeholder="输入服务器地址" />
          </Form.Item>

          <Form.Item
            name="port"
            label="端口号"
            rules={[{ required: true, message: '请输入端口号' }]}
          >
            <InputNumber min={1} max={65535} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="useTls"
            label="使用 TLS 加密"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            name="username"
            label="用户名（可选）"
          >
            <Input placeholder="如果MQTT服务器需要认证，请填写用户名" />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码（可选）"
          >
            <Input.Password placeholder="如果MQTT服务器需要认证，请填写密码" />
          </Form.Item>

          <Form.Item
            name="enabled"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            name="isDefault"
            label="设为默认服务器"
            valuePropName="checked"
            extra="默认服务器将用于发送命令等操作"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea rows={3} placeholder="可选的描述信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MqttServerManagement;
